import { useState } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import AircraftSilhouette from '@/components/AircraftSilhouette';

const NAV = [
  { to: '/', label: 'Dashboard', end: true, icon: 'M3 12l9-9 9 9M5 10v10h5v-6h4v6h5V10' },
  { to: '/flights', label: 'Flight Log', icon: 'M4 6h16M4 12h16M4 18h10' },
  { to: '/history', label: 'History', icon: 'M3 3v5h5M3.05 13a9 9 0 105-8.5L3 8M12 7v5l3 3' },
  { to: '/roster', label: 'Roster', icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 108 0 4 4 0 00-8 0M23 21v-2a4 4 0 00-3-3.87' },
  { to: '/flightaware', label: 'Aircraft Tracking', icon: 'M2 12l20-8-8 20-2-8-8-2z' },
  { to: '/harassment', label: 'Notifications', icon: 'M6 8a6 6 0 1112 0c0 7 3 9 3 9H3s3-2 3-9M10 21h4' },
  { to: '/coowner', label: 'Co-owner', icon: 'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0H5a2 2 0 01-2-2v-4m6 6h10a2 2 0 002-2v-4M3 9h18M3 15h18' },
  { to: '/analytics', label: 'Analytics', icon: 'M4 20V4M4 20h16M8 16v-4M12 16V8M16 16v-6' },
];

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <>
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            [
              'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition',
              isActive
                ? 'bg-avio/10 text-white ring-1 ring-avio/20'
                : 'text-slate-400 hover:bg-white/5 hover:text-slate-100',
            ].join(' ')
          }
        >
          {({ isActive }) => (
            <>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={isActive ? 'h-5 w-5 text-avio' : 'h-5 w-5 text-slate-500 group-hover:text-slate-300'}
              >
                <path d={item.icon} />
              </svg>
              {item.label}
            </>
          )}
        </NavLink>
      ))}
    </>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-avio/10 ring-1 ring-avio/20">
        <AircraftSilhouette className="h-6 w-6 text-avio" />
      </div>
      <div>
        <p className="text-sm font-semibold leading-tight text-white">FlightTrack</p>
        <p className="ident text-[10px] text-avio">N313G</p>
      </div>
    </div>
  );
}

function UserFooter({ onAction }: { onAction?: () => void }) {
  const { user, logout } = useAuth();
  return (
    <div className="border-t border-white/5 p-3">
      {user && (
        <Link to="/account" onClick={onAction} className="mb-2 flex items-center gap-2 rounded-lg px-2 py-1.5 transition hover:bg-white/5" title="Account settings">
          <div className="grid h-8 w-8 place-items-center rounded-full bg-avio/15 text-xs font-semibold text-avio">
            {user.email.slice(0, 2).toUpperCase()}
          </div>
          <p className="truncate text-xs text-slate-400">{user.email}</p>
        </Link>
      )}
      <button
        onClick={() => {
          onAction?.();
          logout();
        }}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 transition hover:bg-white/5 hover:text-slate-100"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
          <path d="M16 17l5-5-5-5M21 12H9M12 19H5a2 2 0 01-2-2V7a2 2 0 012-2h7" />
        </svg>
        Sign out
      </button>
    </div>
  );
}

const AircraftBadge = (
  <div className="mx-4 mb-2 rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3">
    <p className="text-[11px] uppercase tracking-wide text-slate-500">Aircraft</p>
    <p className="text-sm font-medium text-slate-200">
      Cirrus SR22T <span className="text-avio">G7+</span>
    </p>
    <p className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400">
      <span className="h-2 w-2 rounded-full bg-gradient-to-br from-slate-300 to-slate-600 ring-1 ring-white/20" />
      Meteorite livery
    </p>
  </div>
);

export default function Layout() {
  const [drawer, setDrawer] = useState(false);
  const location = useLocation();

  return (
    <div className="flex h-screen overflow-hidden bg-night-900 bg-night-radial text-slate-200">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 flex-col border-r border-white/5 bg-night-850/80 backdrop-blur-xl lg:flex">
        <div className="px-5 py-5">
          <Brand />
        </div>
        {AircraftBadge}
        <nav className="mt-2 flex-1 space-y-1 px-3">
          <NavItems />
        </nav>
        <UserFooter />
      </aside>

      {/* Mobile drawer + backdrop */}
      {drawer && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDrawer(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-72 flex-col border-r border-white/10 bg-night-850 shadow-2xl">
            <div className="flex items-center justify-between px-5 py-5">
              <Brand />
              <button
                onClick={() => setDrawer(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-white/10 hover:text-white"
                aria-label="Close menu"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
            {AircraftBadge}
            <nav className="mt-2 flex-1 space-y-1 px-3">
              <NavItems onNavigate={() => setDrawer(false)} />
            </nav>
            <UserFooter onAction={() => setDrawer(false)} />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="flex items-center gap-3 border-b border-white/5 bg-night-850/80 px-4 py-3 backdrop-blur-xl lg:hidden">
          <button
            onClick={() => setDrawer(true)}
            className="rounded-lg p-1.5 text-slate-300 hover:bg-white/10"
            aria-label="Open menu"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <AircraftSilhouette className="h-6 w-6 text-avio" />
            <span className="text-sm font-semibold text-white">FlightTrack</span>
            <span className="ident text-[10px] text-avio">N313G</span>
          </div>
        </header>

        <main key={location.pathname} className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
